import React from 'react';
import pureEnhance from '@ux/pure-enhance';
import { defaultProps, mapProps } from '@team-griffin/rehook';
import { defaultTo, invoker, pipe, equals, replace, flip, startsWith, any, contains, cond, has, assoc, converge, identity, T, ifElse, propEq, when, both, propSatisfies, tail, omit, path } from 'ramda';
import { renameKeys } from 'ramda-adjunct';
import { Link as Link$1 } from 'react-router-dom';
import { scroller } from 'react-scroll';
import isAbsoluteUrl from 'is-absolute-url';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

var stringFallback = defaultTo('');
var firstChar = invoker(1, 'charAt')(0);
var isProxyUrl = function isProxyUrl(host, proxies, to) {
  return pipe(stringFallback, replace(host, ''), flip(startsWith), // @ts-ignore
  flip(any)(proxies))(to);
};
var isExternalUrl = function isExternalUrl(host, proxies, to) {
  if (isProxyUrl(host, proxies, to)) {
    return true;
  }

  if (contains(host, to)) {
    return false;
  }

  return isAbsoluteUrl(to);
};
var isHashUrl = pipe(stringFallback, firstChar, equals('#'));
var getLinkPath = function getLinkPath(host, proxies, to) {
  if (isExternalUrl(host, proxies, to)) {
    return to;
  }

  return replace(host, '', to);
};

var getExternalFlag = cond([[has('href'), assoc('external', true)], [has('to'), converge(assoc('external'), [function (_ref) {
  var externalRoutes = _ref.externalRoutes,
      host = _ref.host,
      to = _ref.to;
  return isExternalUrl(host, externalRoutes, to);
}, identity])], [T, identity]]);
var getComponentProps = cond([[has('href'), assoc('DynamicLink', 'a')], [has('to'), ifElse(propEq('external', true), pipe(assoc('DynamicLink', 'a'), renameKeys({
  to: 'href'
})), pipe(assoc('DynamicLink', Link$1), function (props) {
  return assoc('to', getLinkPath(props.host, props.externalRoutes, props.to), props);
}))], [T, assoc('DynamicLink', 'button')]]);
var addScrolling = when(both(propEq('external', false), propSatisfies(isHashUrl, 'to')), function (props) {
  return assoc('onClick', function () {
    var scroller = props.scroller,
        to = props.to;
    scroller.scrollTo(tail(to), {
      duration: 500,
      delay: 100,
      smooth: true
    });

    if (props.onClick) {
      props.onClick.apply(props, arguments);
    }
  }, props);
});
var dropInternalProps = omit(['scroller', 'externalRoutes', 'host']);
var hook = pipe(function (props) {
  return defaultProps({
    externalRoutes: [],
    scroller: scroller,
    host: path(['location', 'origin'], window)
  })(props);
}, mapProps(getExternalFlag), mapProps(getComponentProps), mapProps(addScrolling), mapProps(dropInternalProps));

var PureLink = function PureLink(_a) {
  var DynamicLink = _a.DynamicLink,
      props = __rest(_a, ["DynamicLink"]);

  return React.createElement(DynamicLink, Object.assign({}, props));
};

var enhance = pureEnhance('DynamicLink', [hook]);
var Link = enhance(PureLink); // eslint-disable-next-line

var hoc = pureEnhance('DynamicLink', [hook]);

export { Link as DynamicLink, hoc as dynamicLinkHoc, hook as dynamicLinkHook };
